import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ProductDetailModal.css";

// Helper function to decode HTML entities and strip HTML tags
const decodeHTMLEntities = (text) => {
  if (!text) return "";

  // Create a temporary element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  const decoded = textarea.value;

  // Remove any HTML tags
  const cleaned = decoded.replace(/<[^>]+>/g, "");

  return cleaned;
};

const getInitialVariant = (product, cartItem) => {
  const variantGroup = product?.variations?.find(v => v.isVariantGroup);
  const options = variantGroup?.options || [];
  if (options.length === 0) {
    return null;
  }

  if (cartItem) {
    const match = options.find((option) => (
      option.id === cartItem.itemId ||
      option.skuCode === cartItem.skuCode ||
      option.title === cartItem.variantTitle
    ));
    if (match) {
      return match;
    }
  }

  return options[0];
};

export default function ProductDetailModal({
  product,
  onClose,
  onAddToCart,
  onReplaceCartItem,
  soldOutItems = {},
  compactMode = false,
  initialCartItem = null,
  isViewOnly = false,
}) {
  const [show, setShow] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(compactMode);
  // Zomato-style: the bottom sheet always starts at quantity 1, regardless of
  // how many of this exact customization already sit in the cart. Tapping
  // "Add" adds this local quantity on top of the existing cart quantity and
  // closes the sheet.
  const [modalQty, setModalQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(() => getInitialVariant(product, initialCartItem));
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState({});
  const modalContentRef = useRef(null);
  const modalBodyRef = useRef(null);
  const modalNameRef = useRef(null);
  const modalVariationsRef = useRef(null);
  const stickyActiveRef = useRef(false);

  if (!product) return null;

  // Reset the local quantity whenever a different product is opened.
  useEffect(() => {
    setModalQty(Math.max(1, Math.floor(Number(initialCartItem?.quantity) || 1)));
  }, [product?.id, initialCartItem?.id]);

  // Check if product is sold out
  const isSoldOut = product.skuCode && (product.skuCode in soldOutItems);

  // Helper: check if an option (from an option set) is sold out via its skuCode
  const isOptionSoldOut = (option) => Boolean(option?.skuCode && (option.skuCode in soldOutItems));

  // Get variant group if it exists
  const variantGroup = product.variations?.find(v => v.isVariantGroup);
  const hasVariants = variantGroup && variantGroup.options.length > 1;
  const hasSingleVariant = variantGroup && variantGroup.options.length === 1;

  // Get non-variant option groups
  const optionGroups = useMemo(
    () => product.variations?.filter(v => !v.isVariantGroup) || [],
    [product]
  );

  // Get option sets for selected variant (if variant is selected)
  const selectedVariantOptionSets = useMemo(() => (
    selectedVariant && selectedVariant.optionSets && selectedVariant.optionSets.length > 0
      ? selectedVariant.optionSets
      : []
  ), [selectedVariant]);
  
  // Calculate display price based on selected variant (including single variants)
  const displayPrice = (hasVariants || hasSingleVariant) && selectedVariant ? selectedVariant.price : product.price;
  const shouldShowStartingAtText = hasVariants && !selectedVariant;  // Show "Starting at" only if multiple variants exist but none selected yet

  // Clean product description
  const cleanDescription = decodeHTMLEntities(product.description);

  // Filter valid nutritions and process portion size
  let validNutritions =
    Array.isArray(product.nutritions) && product.nutritions.length > 0
      ? product.nutritions.filter(
          (nutri) => nutri.nutritionName && nutri.nutritionValue
        )
      : [];
      

  // Extract portion size and unit
  const portionSizeEntry = validNutritions.find(
    (nutri) => nutri.nutritionName.toLowerCase() === "portionsize"
  );
  const portionSizeUnitEntry = validNutritions.find(
    (nutri) => nutri.nutritionName.toLowerCase() === "portionsizeunit"
  );

  // If both exist, combine them and remove originals
  if (portionSizeEntry && portionSizeUnitEntry) {
    // Remove the original entries
    validNutritions = validNutritions.filter(
      (nutri) =>
        nutri.nutritionName.toLowerCase() !== "portionsize" &&
        nutri.nutritionName.toLowerCase() !== "portionsizeunit"
    );

    // Add combined entry
    validNutritions.unshift({
      nutritionName: "Portion",
      nutritionValue: `${portionSizeEntry.nutritionValue} ${portionSizeUnitEntry.nutritionValue}`,
    });
  }

  // Process allergyInformation to capitalize each item
  const allergyInfoIndex = validNutritions.findIndex(
    (nutri) => nutri.nutritionName.toLowerCase() === "allergyinformation"
  );

  if (allergyInfoIndex !== -1) {
    const allergyInfo = validNutritions[allergyInfoIndex];
    const capitalizedAllergies = allergyInfo.nutritionValue
      .split(',')
      .map(item => item.trim())
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .join(', ');

    validNutritions[allergyInfoIndex] = {
      ...allergyInfo,
      nutritionValue: capitalizedAllergies
    };
  }

  // Fallback images
  const fallbackImages = [
    "https://images.unsplash.com/photo-1509042239860-f550ce710b93?fit=crop&w=400&h=225&q=80",
    "https://images.unsplash.com/photo-1511920170033-f8396924c348?fit=crop&w=400&h=225&q=80",
    "https://images.unsplash.com/photo-1523473827532-4d1e1a2f1c2b?fit=crop&w=400&h=225&q=80",
  ];

  const hasProductImage = Boolean(product.imageUrl || product.productDefaultImage || product.categoryImage);
  const imageSrc =
    product.imageUrl ||
    product.productDefaultImage ||
    product.categoryImage ||
    fallbackImages[Math.floor(Math.random() * fallbackImages.length)];

  useEffect(() => {
    if (!variantGroup || !variantGroup.options || variantGroup.options.length === 0) {
      setSelectedVariant(null);
      return;
    }

    setSelectedVariant((prev) => {
      const initialVariant = getInitialVariant(product, initialCartItem);
      if (initialCartItem && initialVariant) {
        return initialVariant;
      }
      // Keep current selection if it still exists and isn't sold out
      if (prev && variantGroup.options.some((option) => option.id === prev.id)) {
        const prevOption = variantGroup.options.find((option) => option.id === prev.id);
        const prevSoldOut = prevOption?.skuCode && (prevOption.skuCode in soldOutItems);
        if (!prevSoldOut) return prev;
      }
      // Pick first non-sold-out variant
      const firstAvailable = variantGroup.options.find(
        (option) => !(option.skuCode && (option.skuCode in soldOutItems))
      );
      return initialVariant || firstAvailable || variantGroup.options[0];
    });
  }, [product, variantGroup, initialCartItem?.id, soldOutItems]);

  const selectedVariantSoldOut = Boolean(selectedVariant?.skuCode && (selectedVariant.skuCode in soldOutItems));
  const getOptionExtraPrice = (option) => {
    const optionPrice = Number(option?.optionPrice);
    if (Number.isFinite(optionPrice)) {
      return optionPrice;
    }

    const basePrice = Number(option?.basePrice);
    const totalPrice = Number(option?.price);
    if (Number.isFinite(totalPrice) && Number.isFinite(basePrice)) {
      return totalPrice - basePrice;
    }

    return 0;
  };

  const activeOptionGroups = useMemo(() => (
    selectedVariant && selectedVariantOptionSets.length > 0
      ? selectedVariantOptionSets
      : (!variantGroup ? optionGroups : [])
  ), [optionGroups, selectedVariant, selectedVariantOptionSets, variantGroup]);

  useEffect(() => {
    if (!Array.isArray(activeOptionGroups) || activeOptionGroups.length === 0) {
      setSelectedOptionsByGroup({});
      return;
    }

    setSelectedOptionsByGroup((prev) => {
      const next = {};
      const initialOptionsByGroup = (initialCartItem?.selectedOptions || []).reduce((acc, option) => {
        if (option?.groupId && option?.id) {
          if (!acc[option.groupId]) acc[option.groupId] = [];
          acc[option.groupId].push(option.id);
        }
        return acc;
      }, {});

      activeOptionGroups.forEach((group) => {
        const groupId = group.optionSetId;
        const min = Number(group.min || 0);
        const max = Number.isFinite(Number(group.max)) ? Number(group.max) : Infinity;
        const validOptionIds = new Set((group.options || []).map((option) => option.id));

        const sourceSelections = initialCartItem
          ? initialOptionsByGroup[groupId]
          : prev[groupId];
        let selections = Array.isArray(sourceSelections)
          ? sourceSelections.filter((id) => validOptionIds.has(id))
          : [];

        if (selections.length < min) {
          const defaultIds = (group.options || [])
            .filter((option) => option.isDefault)
            .filter((option) => !isOptionSoldOut(option))
            .map((option) => option.id);
          const fallbackIds = (group.options || [])
            .filter((option) => !isOptionSoldOut(option))
            .map((option) => option.id);
          const source = [...defaultIds, ...fallbackIds];

          source.forEach((optionId) => {
            if (selections.length >= min || selections.length >= max) {
              return;
            }
            if (!selections.includes(optionId)) {
              selections.push(optionId);
            }
          });
        }

        next[groupId] = selections.slice(0, Number.isFinite(max) ? max : selections.length);
      });

      return next;
    });
  }, [activeOptionGroups, initialCartItem?.id]);

  const toggleOptionSelection = (group, option) => {
    // Don't allow toggling sold-out options
    if (isOptionSoldOut(option)) return;

    const groupId = group.optionSetId;
    const min = Number(group.min || 0);
    const max = Number.isFinite(Number(group.max)) ? Number(group.max) : Infinity;

    setSelectedOptionsByGroup((prev) => {
      const current = Array.isArray(prev[groupId]) ? prev[groupId] : [];
      const isSelected = current.includes(option.id);
      let nextSelection = [...current];

      if (max === 1) {
        if (isSelected) {
          nextSelection = min === 0 ? [] : [option.id];
        } else {
          nextSelection = [option.id];
        }
      } else if (isSelected) {
        if (current.length > min) {
          nextSelection = current.filter((id) => id !== option.id);
        }
      } else if (current.length < max) {
        nextSelection = [...current, option.id];
      }

      return {
        ...prev,
        [groupId]: nextSelection,
      };
    });
  };

  const selectedOptions = activeOptionGroups.flatMap((group) => {
    const selectedIds = selectedOptionsByGroup[group.optionSetId] || [];
    const selectedInGroup = (group.options || []).filter((option) => selectedIds.includes(option.id));

    // Exclude sold-out options from the final selection
    return selectedInGroup
      .filter((option) => !isOptionSoldOut(option))
      .map((option) => ({
        id: option.id,
        optionId: option.optionId || option.id,
        itemId: option.itemId || "",
        skuCode: option.skuCode || option.id,
        name: option.name || option.title,
        groupName: group.optionSetName || group.optionSetId,
        groupId: group.optionSetId,
        title: option.title,
        price: getOptionExtraPrice(option),
        unitPrice: getOptionExtraPrice(option),
      }));
  });

  const selectedOptionsExtra = selectedOptions.reduce((sum, option) => sum + Number(option.price || 0), 0);
  const finalDisplayPrice = displayPrice + selectedOptionsExtra;
  const hasVariantSpecificOptionSets = selectedVariant && selectedVariantOptionSets.length > 0;
  const shouldRenderVariations = hasVariants || hasVariantSpecificOptionSets || (!variantGroup && optionGroups.length > 0);

  const hasMissingRequiredOptions = activeOptionGroups.some((group) => {
    const min = Number(group.min || 0);
    const selectedIds = (selectedOptionsByGroup[group.optionSetId] || []);
    const availableOptions = (group.options || []).filter((opt) => !isOptionSoldOut(opt));
    // If all options in a required group are sold out, we can't satisfy min.
    // If there are available options but the user hasn't selected enough, also block.
    if (availableOptions.length === 0 && min > 0) return true;
    return selectedIds.length < min;
  });

  const canAddToCart = !isSoldOut && !selectedVariantSoldOut && !hasMissingRequiredOptions;

  const handleAddToCart = () => {
    if (!canAddToCart || !onAddToCart) {
      return;
    }
    const chosenVariant = (hasVariants || hasSingleVariant) ? selectedVariant : null;
    if (initialCartItem && onReplaceCartItem) {
      onReplaceCartItem(initialCartItem.id, product, chosenVariant, selectedOptions, modalQty);
      handleClose();
      return;
    }
    onAddToCart(product, chosenVariant, selectedOptions, modalQty);
    // Zomato-style: close the sheet once the chosen quantity has been added.
    handleClose();
  };

  // Step the local (sheet) quantity. It never goes below 1 so the user is
  // always adding at least one item when they tap "Add".
  const handleStepModalQty = (delta) => {
    setModalQty((prev) => Math.max(1, prev + delta));
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    // Save current scroll position
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;

    // Prevent scrolling
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    // Add class to prevent iOS bouncing on both html and body
    body.classList.add('modal-open');
    html.classList.add('modal-open');

    // Show modal with slight delay for smooth animation
    const timer = setTimeout(() => setShow(true), 10);

    // Cleanup function
    return () => {
      // Restore scrolling
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';
      body.classList.remove('modal-open');
      html.classList.remove('modal-open');

      // Restore scroll position
      window.scrollTo(0, scrollY);
      clearTimeout(timer);
    };
  }, []);

  // Close modal with Escape key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, []);

  // Observe modal-name visibility to show/hide sticky header using scroll
  useEffect(() => {
    const modalContent = modalContentRef.current;
    const modalBody = modalBodyRef.current;
    const modalName = modalNameRef.current;

    if (!modalContent || !modalBody || !modalName) {
      return undefined;
    }

    stickyActiveRef.current = false;
    setShowStickyHeader(false);

    let rafId = null;

    // On desktop (>650px), modal-body scrolls. On mobile, modal-content scrolls.
    const getScrollContainer = () => {
      return window.innerWidth > 650 ? modalBody : modalContent;
    };

    const updateStickyHeader = () => {
      const scrollContainer = getScrollContainer();
      const containerRect = scrollContainer.getBoundingClientRect();
      const nameRect = modalName.getBoundingClientRect();

      const isNameAboveViewport = nameRect.bottom <= containerRect.top + 8;
      const isNameWellVisible = nameRect.top >= containerRect.top + 40;

      if (!stickyActiveRef.current && isNameAboveViewport) {
        stickyActiveRef.current = true;
        setShowStickyHeader(true);
      } else if (stickyActiveRef.current && isNameWellVisible) {
        stickyActiveRef.current = false;
        setShowStickyHeader(false);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateStickyHeader();
      });
    };

    const handleResize = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      stickyActiveRef.current = false;
      setShowStickyHeader(false);

      // Remove old listeners
      modalContent.removeEventListener('scroll', handleScroll);
      modalBody.removeEventListener('scroll', handleScroll);

      // Add listener to correct element
      const scrollContainer = getScrollContainer();
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

      window.requestAnimationFrame(() => {
        updateStickyHeader();
      });
    };

    // Add scroll listener to the correct element based on screen size
    const scrollContainer = getScrollContainer();
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Initial state after layout stabilizes
    window.requestAnimationFrame(() => {
      updateStickyHeader();
    });

    return () => {
      modalContent.removeEventListener('scroll', handleScroll);
      modalBody.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [product]);

  // Touch handlers for drag-to-close (mobile only)
  const handleTouchStart = (e) => {
    // Only enable drag on mobile devices
    if (window.innerWidth > 650) return;

    // Always set the drag start position
    setDragStart(e.touches[0].clientY);

    const modalContent = e.currentTarget;
    const scrollTop = modalContent.scrollTop;

    // Only enable dragging if at the top of the scroll
    if (scrollTop <= 0) {
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (window.innerWidth > 650) return;

    const modalContent = e.currentTarget;
    const scrollTop = modalContent.scrollTop;
    const currentY = e.touches[0].clientY;
    const diff = currentY - dragStart;

    // Prevent overscroll at the top (rubber banding) - this is critical for iOS
    if (scrollTop <= 0 && diff > 0) {
      e.preventDefault();

      // If we're in drag mode, update the drag offset
      if (isDragging) {
        setDragOffset(diff);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging || window.innerWidth > 650) return;

    setIsDragging(false);

    // Close if dragged more than 100px
    if (dragOffset > 100) {
      handleClose();
    }

    // Reset drag offset
    setDragOffset(0);
  };

  const handleClose = () => {
    setIsClosing(true);
    setShow(false);
    // Delay the actual close to allow animation to complete
    setTimeout(() => {
      onClose();
    }, 800);
  };

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Convert camelCase to Title Case
  const toTitleCase = (str) => {
    return str
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (match) => match.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Calculate opacity for overlay based on drag
  const overlayOpacity = dragOffset > 0 ? Math.max(0, 1 - dragOffset / 300) : 1;

  // Zomato-style meta label for a customization group (Required/Optional + count).
  // Hidden in view-only mode since there's nothing to choose.
  const renderOptionSetMeta = (group) => {
    if (!group || isViewOnly) return null;
    const min = Number(group.min || 0);
    const maxRaw = Number(group.max);
    const max = Number.isFinite(maxRaw) ? maxRaw : Infinity;
    const required = min > 0;
    let countLabel = "";
    if (Number.isFinite(max) && max === 1 && min === 1) countLabel = "Choose 1";
    else if (Number.isFinite(max) && min === 0) countLabel = `Select up to ${max}`;
    else if (Number.isFinite(max)) countLabel = `Select ${min}-${max}`;
    else if (min > 0) countLabel = `Select ${min}+`;
    const label = required ? "Required" : "Optional";
    return (
      <span className="option-set-meta">
        {label}{countLabel ? ` • ${countLabel}` : ""}
      </span>
    );
  };

  return (
    <div
      className={`modal-overlay ${show ? "show" : ""} ${isClosing ? "closing" : ""}`}
      onClick={handleOverlayClick}
      style={{
        backgroundColor: `rgba(0, 0, 0, ${0.5 * overlayOpacity})`
      }}
    >
      {/* Close button */}
      <button className={`modal-close-btn ${compactMode ? "compact-mode" : ""}`} onClick={handleClose}>
          ×
        </button>
      <div
        className={`modal-content ${show ? "show" : ""} ${isClosing ? "closing" : ""} ${isDragging ? "dragging" : ""} ${!hasProductImage ? "no-image" : ""} ${compactMode ? "compact-mode" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={modalContentRef}
        style={{
          transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
          transition: isDragging ? 'none' : undefined
        }}
      >
        {/* Product Image Container - hidden in compact mode or when no image */}
        {!compactMode && hasProductImage && (
        <div className="modal-image-container">
          {isSoldOut && <div className="modal-sold-out-badge-floating">Sold Out</div>}
          <img
            src={imageSrc}
            alt={product.name}
            className="modal-image"
            onError={(e) => (e.target.src = fallbackImages[0])}
          />
          {isSoldOut && <div className="modal-sold-out-overlay"></div>}
        </div>
        )}

        {/* Modal Details */}
        <div className="modal-detail-panel">
          <div className="modal-body" ref={modalBodyRef}>
            {/* Sticky Header - appears when scrolling (always shown in compact mode) */}
            <div className={`sticky-header-wrapper ${(showStickyHeader || compactMode) ? "visible" : ""}`}>
              <div
                className={`sticky-header ${(showStickyHeader || compactMode) ? "visible" : ""}`}
                aria-hidden={!(showStickyHeader || compactMode)}
              >
                {hasProductImage && (
                <img
                  src={imageSrc}
                  alt={product.name}
                  className="sticky-header-image"
                  onError={(e) => (e.target.src = fallbackImages[0])}
                />
                )}
                <div className="sticky-header-info">
                  <h3 className="sticky-header-title">
                    {product.name}
                  </h3>
                  <p className="sticky-header-price">
                    {shouldShowStartingAtText && <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginRight: '4px' }}>Starting at</span>}
                    ₹{Math.round(finalDisplayPrice)}
                  </p>
                </div>
                {/* Sticky Header Tags - on the right */}
                {product.tags && product.tags.length > 0 && (() => {
                  const hasEggTag = product.tags.some(tag =>
                    tag?.name?.toLowerCase().includes('egg')
                  );
                  const hasNonVegTag = product.tags.some(tag => {
                    const name = tag?.name?.toLowerCase() || '';
                    return name.includes('non') && name.includes('veg');
                  });

                  return (
                    <div className="sticky-header-tags">
                      {hasEggTag && hasNonVegTag ? <img
                          src="/EggTag.png"
                          alt="Egg (Non-Veg)"
                          className="sticky-header-tag-icon"
                        /> : ''}
	                      {product.tags.map((tag, index) => {
	                        const tagKey = tag?.id || tag?.itemTagId || tag?.name || index;
	                        if(hasEggTag && hasNonVegTag && (tag?.name?.toLowerCase() === 'egg' || tag?.name?.toLowerCase().includes('non'))){
	                          return null;
	                        }
	                        else if (tag?.tagIcon) {
	                          // Use NewTag.png from public folder for the New tag
	                          if (tag?.id === '6932636dec4624b7afca0266') {
	                            return (
	                              <img
	                                key={tagKey}
	                                src="/NewTag.png"
	                                alt={tag?.name || 'New'}
	                                className="sticky-header-tag-icon"
                              />
                            );
                          }
	                          return (
	                            <img
	                              key={tagKey}
	                              src={tag?.tagIcon?.fullPath}
	                              alt={tag?.name}
	                              className="sticky-header-tag-icon"
                            />
                          );
                        }
                        return null;
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

          {/* Product Tags, Name, Price, Description, Nutrition - hidden in compact mode */}
          {!compactMode && (
          <>
          {/* Product Tags */}
          {product.tags && product.tags.length > 0 && (() => {
            // Check if product has both egg and non-veg tags
            const hasEggTag = product.tags.some(tag =>
              tag?.name?.toLowerCase().includes('egg')
            );
            const hasNonVegTag = product.tags.some(tag => {
              const name = tag?.name?.toLowerCase() || '';
              return name.includes('non') && name.includes('veg');
            });

            // Otherwise, show all tags normally
            return (
              <div className="modal-tags-container">
                {(hasEggTag && hasNonVegTag) ? (
                        <div className="modal-egg-nonveg-container">
                          <img
                            src="/EggTag.png"
                            alt="Egg (Non-Veg)"
                            className="modal-egg-nonveg-icon"
                          />
                        </div>
                      ) : ''}
	                {product.tags.map((tag, index) => {
	                  const tagKey = tag?.id || tag?.itemTagId || tag?.name || index;
	                  if(hasEggTag && hasNonVegTag && (tag?.name?.toLowerCase() === 'egg' || tag?.name?.toLowerCase().includes('non'))){
	                    return null;
	                  }
	                  else if (tag?.tagIcon) {
	                    // Use NewTag.png from public folder for the New tag
	                    if (tag?.id === '6932636dec4624b7afca0266') {
	                      return (
	                        <div key={tagKey} className="modal-egg-nonveg-container">
	                        <img
	                          src="/NewTag.png"
	                          alt={tag?.name || 'New'}
	                          className="modal-egg-nonveg-icon"
                        />
                        </div>
                      );
                    }
	                    return (
	                      <img
	                        key={tagKey}
	                        src={tag?.tagIcon?.fullPath}
	                        alt={tag?.name}
	                        className="modal-tag-icon"
                      />
                    );
                  }
                  return null;
                })}
              </div>
            );
          })()}

          {/* Product Name */}
          <h2
            className={`modal-name`}
            ref={modalNameRef}
          >
            {product.name}
          </h2>

          {/* Price - moved before description for better hierarchy */}
          <p className="modal-price">
            {shouldShowStartingAtText && <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginRight: '6px' }}>Starting at</span>}
            ₹{Math.round(finalDisplayPrice)}
          </p>

          {/* Description */}
          {cleanDescription && (
            <div className="modal-description-container">
              <p className="modal-description">
                {cleanDescription}
              </p>
            </div>
          )}

          {validNutritions.length > 0 && (
            <div className="modal-nutrition">
              <h4>Nutrition Facts</h4>
              <table className="nutrition-table">
                <tbody>
                  {validNutritions.map((nutri, idx) => (
                    <tr key={idx}>
                      <td className="nutri-name">{toTitleCase(nutri.nutritionName)}</td>
                      <td className="nutri-value">
                        {nutri.nutritionName === "Portion" ||
                         nutri.nutritionName.toLowerCase() === "allergyinformation"
                          ? nutri.nutritionValue
                          : nutri.units ? `${nutri.nutritionValue}${nutri.units}` : `${nutri.nutritionValue}${nutri.nutritionName.toLowerCase() === 'sodium' ? 'm' : ''}g`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          </>
          )}

          {/* Variations Section */}
          {shouldRenderVariations && (
            <div className={`modal-variations ${isViewOnly ? "view-only" : ""}`} ref={modalVariationsRef}>
              {/* Only show the variants heading when the user has an actual choice to make. */}
              {hasVariants && <h4>Available Options</h4>}
              <div className="variations-list">
                {/* Variant Group - Show as selectable buttons only if multiple variants exist. */}
                {hasVariants && (
                  <div className="variation-group">
                    <div className="option-set-header">
                      <h5 className="option-set-name">{variantGroup.optionSetName}</h5>
                      {!isViewOnly && <span className="option-set-meta">Required • Choose 1</span>}
                    </div>

                    <div className="variant-selector">
                      {variantGroup.options.map((variant) => {
                        const variantSoldOut = Boolean(variant.skuCode && (variant.skuCode in soldOutItems));
                        return (
                        <button
                          key={variant.id}
                          className={`variant-button ${(!isViewOnly && selectedVariant?.id === variant.id) ? 'active' : ''} ${variantSoldOut ? 'variant-sold-out' : ''}`}
                          onClick={(isViewOnly || variantSoldOut) ? undefined : () => setSelectedVariant(variant)}
                          disabled={isViewOnly || variantSoldOut}
                        >
                          <span className="variant-button-title">{variant.title}</span>
                          {variantSoldOut ? (
                            <span className="variant-button-price variant-sold-out-text">Sold Out</span>
                          ) : (
                            <span className="variant-button-price">₹{Math.round(variant.price)}</span>
                          )}
                        </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Show variant-specific option sets (when a variant is selected, including single variants) */}
                {hasVariantSpecificOptionSets && (
                  <>
                    {selectedVariantOptionSets.map((optionGroup) => (
                      <div key={optionGroup.optionSetId} className="variation-group">
                        <div className="option-set-header">
                          <h5 className="option-set-name">{optionGroup.optionSetName}</h5>
                          {renderOptionSetMeta(optionGroup)}
                        </div>

                        <div className="options-group">
                          {optionGroup.options.map((option) => {
                            // Check if option has both egg and non-veg tags
                            const hasEggTag = option.tags?.some(tag =>
                              tag?.name?.toLowerCase().includes('egg')
                            );
                            const hasNonVegTag = option.tags?.some(tag => {
                              const name = tag?.name?.toLowerCase() || '';
                              return name.includes('non') && name.includes('veg');
                            });
                            const hasEggAndNonVeg = hasEggTag && hasNonVegTag;

                            const selectedIds = selectedOptionsByGroup[optionGroup.optionSetId] || [];
                            const isSelected = selectedIds.includes(option.id);
                            const extraPrice = getOptionExtraPrice(option);
                            const optionSoldOut = isOptionSoldOut(option);
                            const optionDisabled = isViewOnly || optionSoldOut;

                            return (
                            <div
                              key={option.id}
                              className={`variation-item option-selectable ${(!optionDisabled && isSelected) ? "selected" : ""} ${optionSoldOut ? "option-sold-out" : ""}`}
                              onClick={optionDisabled ? undefined : () => toggleOptionSelection(optionGroup, option)}
                              role={optionDisabled ? undefined : "button"}
                              tabIndex={optionDisabled ? undefined : 0}
                              aria-disabled={optionSoldOut || undefined}
                              onKeyDown={optionDisabled ? undefined : (event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  toggleOptionSelection(optionGroup, option);
                                }
                              }}
                            >
                              <div className="variation-header">
                                <div className="variation-title-with-tags">
                                  {/* Display option tags if available */}
                                  {option.tags && option.tags.length > 0 && (
                                    <div className="option-tags-container">
                                      {/* Show combined egg+non-veg icon if both tags present */}
                                      {hasEggAndNonVeg && (
                                        <img
                                          src="/EggTag.png"
                                          alt="Egg (Non-Veg)"
                                          className="option-tag-icon egg-icon"
                                        />
                                      )}
                                      {/* Show other tags, excluding egg and non-veg if they're combined */}
                                      {option.tags.map((tag, idx) => {
                                        // Skip egg and non-veg tags if we're showing the combined icon
                                        if (hasEggAndNonVeg && (
                                          tag?.name?.toLowerCase().includes('egg') ||
                                          (tag?.name?.toLowerCase().includes('non') && tag?.name?.toLowerCase().includes('veg'))
                                        )) {
                                          return null;
                                        }
                                        return tag?.tagIcon && (
                                          <img
                                            key={idx}
                                            src={tag.tagIcon.fullPath}
                                            alt={tag.name}
                                            className="option-tag-icon"
                                          />
                                        );
                                      })}
                                    </div>
                                  )}
                                  <span className="variation-title">{option.title}</span>
                                </div>
                                <span className="variation-price">
                                  {optionSoldOut ? (
                                    <span className="option-sold-out-text">Sold Out</span>
                                  ) : (
                                    /* For option set items, show price difference with +. For variants, show full price */
                                    option.isOptionSet ? (
                                      extraPrice > 0 ? `+₹${Math.round(extraPrice)}` : ''
                                    ) : (
                                      `₹${Math.round(option.price)}`
                                    )
                                  )}
                                </span>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Show non-variant option groups (same for all variants) - but only if there's no variant group */}
                {!variantGroup && optionGroups.map((optionGroup) => (
                  <div key={optionGroup.optionSetId} className="variation-group">
                    <div className="option-set-header">
                      <h5 className="option-set-name">{optionGroup.optionSetName}</h5>
                      {renderOptionSetMeta(optionGroup)}
                    </div>

                    <div className="options-group">
                      {optionGroup.options.map((option) => {
                        // Check if option has both egg and non-veg tags
                        const hasEggTag = option.tags?.some(tag =>
                          tag?.name?.toLowerCase().includes('egg')
                        );
                        const hasNonVegTag = option.tags?.some(tag => {
                          const name = tag?.name?.toLowerCase() || '';
                          return name.includes('non') && name.includes('veg');
                        });
                        const hasEggAndNonVeg = hasEggTag && hasNonVegTag;

                        const selectedIds = selectedOptionsByGroup[optionGroup.optionSetId] || [];
                        const isSelected = selectedIds.includes(option.id);
                        const extraPrice = getOptionExtraPrice(option);
                        const optionSoldOut = isOptionSoldOut(option);
                        const optionDisabled = isViewOnly || optionSoldOut;

                        return (
                        <div
                          key={option.id}
                          className={`variation-item option-selectable ${(!optionDisabled && isSelected) ? "selected" : ""} ${optionSoldOut ? "option-sold-out" : ""}`}
                          onClick={optionDisabled ? undefined : () => toggleOptionSelection(optionGroup, option)}
                          role={optionDisabled ? undefined : "button"}
                          tabIndex={optionDisabled ? undefined : 0}
                          aria-disabled={optionSoldOut || undefined}
                          onKeyDown={optionDisabled ? undefined : (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleOptionSelection(optionGroup, option);
                            }
                          }}
                        >
                          <div className="variation-header">
                            <div className="variation-title-with-tags">
                              {/* Display option tags if available */}
                              {option.tags && option.tags.length > 0 && (
                                <div className="option-tags-container">
                                  {/* Show combined egg+non-veg icon if both tags present */}
                                  {hasEggAndNonVeg && (
                                    <img
                                      src="/EggTag.png"
                                      alt="Egg (Non-Veg)"
                                      className="option-tag-icon egg-icon"
                                    />
                                  )}
                                  {/* Show other tags, excluding egg and non-veg if they're combined */}
                                  {option.tags.map((tag, idx) => {
                                    // Skip egg and non-veg tags if we're showing the combined icon
                                    if (hasEggAndNonVeg && (
                                      tag?.name?.toLowerCase().includes('egg') ||
                                      (tag?.name?.toLowerCase().includes('non') && tag?.name?.toLowerCase().includes('veg'))
                                    )) {
                                      return null;
                                    }
                                    return tag?.tagIcon && (
                                      <img
                                        key={idx}
                                        src={tag.tagIcon.fullPath}
                                        alt={tag.name}
                                        className="option-tag-icon"
                                      />
                                    );
                                  })}
                                </div>
                              )}
                              <span className="variation-title">{option.title}</span>
                            </div>
                            <span className="variation-price">
                              {optionSoldOut ? (
                                <span className="option-sold-out-text">Sold Out</span>
                              ) : (
                                /* For option set items, show price difference with +. For variants, show full price */
                                option.isOptionSet ? (
                                  extraPrice > 0 ? `+₹${Math.round(extraPrice)}` : ''
                                ) : (
                                  `₹${Math.round(option.price)}`
                                )
                              )}
                            </span>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
        {onAddToCart && (
          <div className="modal-sticky-cart-bar">
            <div className="modal-sticky-qty-controls">
              <button
                className="modal-sticky-qty-btn"
                onClick={() => handleStepModalQty(-1)}
                aria-label={`Remove one ${product.name}`}
                disabled={!canAddToCart || modalQty <= 1}
              >
                −
              </button>
              <span className="modal-sticky-qty-value">{modalQty}</span>
              <button
                className="modal-sticky-qty-btn"
                onClick={() => handleStepModalQty(1)}
                aria-label={`Add one ${product.name}`}
                disabled={!canAddToCart}
              >
                +
              </button>
            </div>
            <button
              className="modal-add-cart-btn sticky"
              onClick={handleAddToCart}
              disabled={!canAddToCart}
            >
              {canAddToCart
                ? `${initialCartItem ? "Update item" : "Add"} • ₹${Math.round(finalDisplayPrice * modalQty)}`
                : "Unavailable"}
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
